import { Component, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { IsAgendamentos, SectorType } from 'src/app/interfaces/sector'
import { SetoresService } from 'src/app/services/setores.service'
import {
  ATALHO_MAX_LENGTH,
  atalhoValidator,
  normalizeAtalho,
} from 'src/app/shared/utils/setor-atalho'

@Component({
  selector: 'app-settings-setores',
  templateUrl: './settings-setores.component.html',
  styleUrls: ['./settings-setores.component.scss'],
})
export class SettingsSetoresComponent implements OnInit {
  public setores: Array<SectorType> = []
  public formSetor!: FormGroup
  public setor: SectorType = {}
  public showSpinner: boolean = false
  public buttonText: String = 'Confirmar'
  /** Erro devolvido pelo backend ao salvar, fora do campo Atalho (#13788). */
  public saveError: string = ''
  public readonly atalhoMaxLength = ATALHO_MAX_LENGTH
  public columns = [
    {
      columnDef: 'descricao',
      header: 'Descrição',
      cell: (element: SectorType) => `${element.descricao}`,
    },
    {
      columnDef: 'prefixo',
      header: 'Prefixo',
      cell: (element: SectorType) => `${element.prefixo}`,
    },
    {
      columnDef: 'botao',
      header: 'Botão',
      cell: (element: SectorType) => `${this.formatBotao(element.botao!)}`,
    },
    {
      columnDef: 'cor',
      header: 'Cor',
      cell: (element: SectorType) => `${element.cor}`,
    },
    {
      columnDef: 'corfonte',
      header: 'Fonte',
      cell: (element: SectorType) => `${element.corfonte}`,
    },
    {
      columnDef: 'atalho',
      header: 'Atalho',
      cell: (element: SectorType) => `${element.atalho || ''}`,
    },
    {
      columnDef: 'is_agendamentos',
      header: 'Agendamentos',
      cell: (element: SectorType) => `${this.formatBotao(element.is_agendamentos!)}`,
    },
    {
      columnDef: 'ordem',
      header: 'Ordem',
      cell: (element: SectorType) => `${element.ordem ?? 0}`,
    },
    {
      columnDef: 'edit',
    },
  ]
  public displayedColumns = this.columns.map((c) => c.columnDef)
  isModalOpen: boolean = false

  constructor(private setorService: SetoresService, private formBuilder: FormBuilder) {}

  ngOnInit(): void {
    this.createForm()
    this.loadSetores()
  }

  formatBotao(element: string | boolean): String {
    if (element === false || element === IsAgendamentos.NAO) return 'Não'
    if (element === IsAgendamentos.DIRECT) return 'Direto'
    return 'Sim'
  }

  loadSetores() {
    this.setorService.getSetores().subscribe((resp) => {
      this.setores = resp
      this.setores.map((item) => {
        if (item.cor === null || item.cor === undefined) {
          item.cor = '#000000'
        }
        if (item.corfonte === null || item.corfonte === undefined) {
          item.corfonte = '#ffffff'
        }
      })
      // RN03/RN04 são checadas contra a lista: revalida o campo quando ela chega.
      this.formSetor?.get('atalho')?.updateValueAndValidity()
    })
  }

  createForm() {
    this.formSetor = this.formBuilder.group({
      id: [this.setor.id ? this.setor.id : undefined],
      descricao: [this.setor.descricao ? this.setor.descricao : '', Validators.required],
      prefixo: [this.setor.prefixo ? this.setor.prefixo : '', Validators.maxLength(3)],
      botao: [this.setor.botao ? this.setor.botao : false],
      cor: [this.setor.cor && this.setor.cor[0] === '#' ? this.setor.cor : '#000000'],
      corfonte: [this.setor.corfonte ? this.setor.corfonte : '#ffffff'],
      is_agendamentos: [this.setor.is_agendamentos || IsAgendamentos.NAO],
      ordem: [this.setor.ordem ?? 0],
      atalho: [
        normalizeAtalho(this.setor.atalho),
        [
          Validators.maxLength(ATALHO_MAX_LENGTH),
          Validators.pattern(/^[0-9A-Z]*$/),
          atalhoValidator(
            () => this.setores,
            () => this.formSetor?.get('id')?.value
          ),
        ],
      ],
    })
  }

  /**
   * RF02 — o campo só registra dígitos e letras, em maiúsculas, até 3 caracteres.
   * Normaliza o valor a cada tecla para que `@`, espaço ou um 4º caractere não apareçam.
   */
  onAtalhoInput(event: Event) {
    const input = event.target as HTMLInputElement
    const control = this.formSetor.get('atalho')
    const normalizado = normalizeAtalho(input.value)
    if (input.value !== normalizado) {
      input.value = normalizado
    }
    if (control && control.value !== normalizado) {
      control.setValue(normalizado)
    }
  }

  submitForm() {
    this.formSetor.get('atalho')?.updateValueAndValidity()
    if (!this.formSetor.valid) {
      this.formSetor.markAllAsTouched()
      return
    }

    this.saveError = ''
    this.showSpinner = true
    const payload: SectorType = {
      ...this.formSetor.value,
      atalho: normalizeAtalho(this.formSetor.value.atalho) || null,
    }
    const request$ = payload.id
      ? this.setorService.updateSetor(payload)
      : this.setorService.insertSetor(payload)

    request$.subscribe(
      () => {
        this.showSpinner = false
        this.cleanForm()
        this.loadSetores()
      },
      (err) => {
        this.showSpinner = false
        this.handleSaveError(err)
      }
    )
  }

  /**
   * Erro do backend ao salvar (#13788): conflito de atalho (RN03/RN04 validadas também
   * no servidor) cai no próprio campo; qualquer outro erro aparece no formulário.
   */
  private handleSaveError(err: any) {
    const body = err?.error ?? {}
    const mensagem: string = body.Erro || body.message || 'Não foi possível salvar o setor'
    if (body.campo === 'atalho') {
      const control = this.formSetor.get('atalho')
      control?.setErrors({ ...(control.errors || {}), atalhoServidor: { mensagem } })
      control?.markAsTouched()
      return
    }
    this.saveError = mensagem
  }

  setSetor(i: number) {
    this.saveError = ''
    this.isModalOpen = true
    if (this.setores.length > 0) {
      this.setor = this.setores[i]
      this.setForm()
    }
  }

  deleteSetor(i: number) {
    if (this.setor) {
      this.showSpinner = true
      this.setorService.deleteSetor(i)
      setTimeout(() => {
        this.cleanForm()
        this.loadSetores()
        this.showSpinner = false
      }, 1000)
    }
  }

  cleanForm(newSetor?: boolean) {
    this.setor = {
      botao: true,
      cor: '#ffffff',
      corfonte: '#000000',
      descricao: '',
      is_agendamentos: IsAgendamentos.NAO,
      ordem: 0,
      atalho: null,
    }
    this.saveError = ''
    this.isModalOpen = !!newSetor
    this.setForm()
  }

  setForm() {
    this.formSetor.controls['id'].setValue(this.setor.id)
    this.formSetor.controls['descricao'].setValue(this.setor.descricao)
    this.formSetor.controls['prefixo'].setValue(this.setor.prefixo)
    this.formSetor.controls['botao'].setValue('' + this.setor.botao + '')
    if (this.setor.cor) {
      this.formSetor.controls['cor'].setValue(this.setor.cor)
    }
    if (this.setor.corfonte) {
      this.formSetor.controls['corfonte'].setValue(this.setor.corfonte)
    }
    this.formSetor.controls['is_agendamentos'].setValue('' + this.setor.is_agendamentos + '')
    this.formSetor.controls['ordem'].setValue(this.setor.ordem ?? 0)
    this.formSetor.controls['atalho'].setValue(normalizeAtalho(this.setor.atalho))
    this.formSetor.controls['atalho'].markAsUntouched()
  }

  getRows() {
    let rows = (this.setores.length - 2) * 10
    if (rows < 0) {
      return 10
    } else {
      return rows
    }
  }
}
